import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import { VoiceDetailsStep } from "../ai-voices/voice-cloning";
import { AudioInput } from "../audio-input";
import { useVoiceCloningStore } from "../../zustand-store/useVoiceCloningStore";
import { useGetPresignedUrl, uploadToS3, useCreateClonedVoice, fetchClonedVoiceById } from "../../http-hooks/ai-voices/useClonedVoice";
import FeatureCreditCost from "../plans/FeatureCreditCost";
import { useFeatureCreditGate } from "../../http-hooks/organisation/useFeatureCreditEstimate";
import { C } from "../../styles/tokens";

// Content-only port of InstantVoiceClone.tsx for use inside a modal/sheet — no page header,
// hero callout, tips banner, or step-progress sidebar; navigation is replaced with onCloneSuccess.
export default function VoiceCloneView({ org_id, integration_id, onCloneSuccess }) {
  const {
    currentStep, setCurrentStep,
    audioFile, audioUrl, audioDuration,
    uploadedAudioUrl, setAudioFile, setUploadedAudioUrl,
    isStep1Valid, isStep2Valid, resetStore,
  } = useVoiceCloningStore();

  const audioValue = audioFile
    ? { file: audioFile, previewUrl: audioUrl ?? "", durationSec: audioDuration || undefined }
    : null;

  const handleAudioChange = (value) => {
    const next = Array.isArray(value) ? value[0] ?? null : value;
    if (next) setAudioFile(next.file, next.previewUrl, next.durationSec ?? 0);
    else setAudioFile(null, null, 0);
  };

  useEffect(() => { resetStore(); }, [resetStore]);

  const getPresignedUrlMutation = useGetPresignedUrl();
  const createClonedVoiceMutation = useCreateClonedVoice();
  const isUploading = createClonedVoiceMutation.isPending;
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);

  const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
    org_id, feature: "voice-clone", enabled: currentStep === 2,
  });

  const [pollingStatus, setPollingStatus] = useState("idle");
  const pollingIntervalRef = useRef(null);
  const pollingAttemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((voiceId) => {
    setPollingStatus("polling");
    pollingAttemptsRef.current = 0;

    pollingIntervalRef.current = setInterval(async () => {
      pollingAttemptsRef.current += 1;

      if (pollingAttemptsRef.current > 40) {
        stopPolling();
        setPollingStatus("timeout");
        return;
      }

      try {
        const voice = await fetchClonedVoiceById(org_id, integration_id, voiceId);
        if (voice?.status === "ready") {
          stopPolling();
          resetStore();
          toast.success("Your voice is ready!");
          onCloneSuccess?.();
        } else if (voice?.status === "failed") {
          stopPolling();
          setPollingStatus("failed");
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);
  }, [org_id, integration_id, stopPolling, resetStore, onCloneSuccess]);

  const handleContinueToStep2 = async () => {
    if (!isStep1Valid() || !audioFile || !org_id || !integration_id) return;
    try {
      if (uploadedAudioUrl) { setCurrentStep(2); return; }
      setIsUploadingAudio(true);
      const presignedData = await getPresignedUrlMutation.mutateAsync({
        org_id, integration_id, fileName: audioFile.name, contentType: audioFile.type,
      });
      await uploadToS3(presignedData.signed_url, audioFile);
      const audioSampleUrl = `${process.env.REACT_APP_CLONED_VOICE_BUCKET_URL || process.env.REACT_APP_AUDIO_PREVIEW_URL}/${presignedData.url}`;
      setUploadedAudioUrl(audioSampleUrl);
      toast.success("Audio uploaded successfully!");
      setCurrentStep(2);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload audio file");
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const handleSaveClonedVoice = async () => {
    if (!uploadedAudioUrl || !org_id || !integration_id) {
      toast.error("Audio file not uploaded. Please go back to Step 1.");
      return;
    }
    try {
      const url = new URL(uploadedAudioUrl);
      const audioSamplePath = url.pathname.substring(1);
      const { voiceDetails } = useVoiceCloningStore.getState();
      const result = await createClonedVoiceMutation.mutateAsync({
        org_id, integration_id, voice_name: voiceDetails.name,
        language_code: voiceDetails.languageCode, description: voiceDetails.description,
        audio_sample_path: audioSamplePath, audio_duration: audioDuration,
      });
      startPolling(result.voiceId);
    } catch (error) {
      console.error("Error creating cloned voice:", error);
      toast.error("Failed to clone voice. Please try again.");
    }
  };

  if (pollingStatus !== "idle") {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: 280, gap: 20 }}>
        {pollingStatus === "polling" && (
          <>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.p600}, ${C.v500})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="spinner-border text-white" style={{ width: 26, height: 26, borderWidth: 3 }} role="status" aria-hidden="true" />
            </div>
            <div>
              <h5 style={{ fontSize: 16, fontWeight: 700, color: C.n900, marginBottom: 6 }}>Preparing your voice…</h5>
              <p style={{ fontSize: 12.5, color: C.n500, marginBottom: 0, maxWidth: 340 }}>
                Your voice is being cloned in the background. This usually takes 1–2 minutes. Please don't close this dialog.
              </p>
            </div>
          </>
        )}

        {(pollingStatus === "failed" || pollingStatus === "timeout") && (
          <>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "#FEF2F2", border: "2px solid #FECACA",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
            }}>
              ✕
            </div>
            <div>
              <h5 style={{ fontSize: 16, fontWeight: 700, color: C.n900, marginBottom: 6 }}>
                {pollingStatus === "timeout" ? "Still processing…" : "Voice cloning failed"}
              </h5>
              <p style={{ fontSize: 12.5, color: C.n500, marginBottom: 0, maxWidth: 360 }}>
                {pollingStatus === "timeout"
                  ? "The voice is taking longer than expected. It may still complete — check My Voices in a few minutes, or contact support if it never appears."
                  : "Something went wrong while cloning your voice. Please try again, or contact support if the problem persists."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (pollingStatus === "timeout") {
                  resetStore();
                  onCloneSuccess?.();
                } else {
                  setPollingStatus("idle");
                }
              }}
              style={{
                background: `linear-gradient(135deg, ${C.p600}, ${C.v500})`,
                color: "white", border: "none", borderRadius: 10,
                padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {pollingStatus === "timeout" ? "Go to My Voices" : "Try Again"}
            </button>
          </>
        )}
      </div>
    );
  }

  if (currentStep === 1) {
    return (
      <div>
        <AudioInput
          value={audioValue}
          onChange={handleAudioChange}
          onError={(e) => toast.error(e.message)}
          validation={{ maxSizeBytes: 15 * 1024 * 1024, allowedMimePrefixes: ["audio/", "video/"], minDurationSec: 10, maxDurationSec: 60 }}
          recording={{ micDeviceSelector: true, maxRecordingDurationSec: 60 }}
        />

        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <span style={{ fontSize: 12, color: C.n500 }}>
            Minimum 10 seconds of clear audio required
          </span>
          <button
            type="button"
            disabled={!isStep1Valid() || isUploadingAudio}
            title={audioDuration > 0 && audioDuration < 10 ? `Audio is ${Math.floor(audioDuration)}s. Minimum 10s required.` : ""}
            onClick={handleContinueToStep2}
            style={{
              alignSelf: "flex-end",
              background: isStep1Valid() && !isUploadingAudio ? `linear-gradient(135deg, ${C.p600}, ${C.v500})` : C.n100,
              color: isStep1Valid() && !isUploadingAudio ? "white" : C.n500,
              border: "none", borderRadius: 10, padding: "9px 22px", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
              cursor: isStep1Valid() && !isUploadingAudio ? "pointer" : "not-allowed",
            }}
          >
            {isUploadingAudio ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Uploading...
              </>
            ) : "Continue →"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <VoiceDetailsStep />

      <div style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <button
          type="button"
          onClick={() => setCurrentStep(1)}
          disabled={isUploading}
          style={{
            background: "transparent", border: `1px solid ${C.n200}`, color: C.n500,
            borderRadius: 10, padding: "9px 18px", fontSize: 13, fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <FeatureCreditCost orgId={org_id} feature="voice-clone" />
        <button
          type="button"
          disabled={!isStep2Valid() || isUploading || hasInsufficientCredits}
          title={!isStep2Valid() ? "Please fill in all required fields and accept the terms" : hasInsufficientCredits ? "Not enough credits to clone a voice" : ""}
          onClick={handleSaveClonedVoice}
          style={{
            background: isStep2Valid() && !hasInsufficientCredits ? `linear-gradient(135deg, ${C.p600}, ${C.v500})` : C.n100,
            color: isStep2Valid() && !hasInsufficientCredits ? "white" : C.n500,
            border: "none", borderRadius: 10, padding: "9px 22px", fontSize: 13, fontWeight: 600,
            display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
            cursor: isStep2Valid() && !hasInsufficientCredits ? "pointer" : "not-allowed",
          }}
        >
          {isUploading ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Saving...
            </>
          ) : "Save Voice"}
        </button>
        </div>
      </div>
    </div>
  );
}
